import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { Subscription } from 'rxjs/Subscription';
import { switchMap } from 'rxjs/operators';
import { ApiService } from '../../../../utils/services/api.service';
import {
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_IMAGE_MAX_BYTES,
  ANNOUNCEMENT_IMAGE_TYPES,
  ANNOUNCEMENT_INCOMPLETE_CODE,
  Announcement,
  AnnouncementStatus,
  AnnouncementStep,
  WHATS_NEW_SCREENS,
  defaultButtonText,
  findScreen,
  isBlank,
  localToday
} from '../whats-new';

const FIELD_LABELS = {
  title: 'Başlık',
  publishDate: 'Yayın tarihi',
  summary: 'Kısa özet',
  whatChanged: 'Ne değişti?'
};

const FIELD_ERRORS = {
  title: 'Başlık boş bırakılamaz.',
  publishDate: 'Yayın tarihini seçin.',
  summary: 'Kısa özet boş bırakılamaz; listede başlığın altında görünür.',
  whatChanged: '“Ne değişti?” alanını doldurun; duyurunun ana anlatımıdır.'
};

const REQUIRED_TO_PUBLISH = ['title', 'publishDate', 'summary', 'whatChanged'];

@Component({
  selector: 'app-whats-new-editor',
  templateUrl: './whats-new-editor.component.html',
  styleUrls: ['./whats-new-editor.component.css']
})
export class WhatsNewEditorComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public announcementId: number = null;
  public feedbackCount = 0;
  public isLoading = false;
  public isSaving = false;
  public previewMode: 'list' | 'detail' = 'detail';
  public errors: { [field: string]: string } = {};
  public errorsFor: AnnouncementStatus = 'Published';
  public shakingField: string = null;
  public uploading: { [key: string]: boolean } = {};
  public categories = ANNOUNCEMENT_CATEGORIES;
  public screens = WHATS_NEW_SCREENS;
  public fieldLabels = FIELD_LABELS;
  private subscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private notifications: NotificationsService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  get steps(): FormArray {
    return this.form.get('steps') as FormArray;
  }

  get errorKeys(): string[] {
    return REQUIRED_TO_PUBLISH.filter(field => this.errors[field]);
  }

  get buttonTextPlaceholder(): string {
    return defaultButtonText(this.form.value.targetPath) || 'Ekran seçince otomatik';
  }

  ngOnInit() {
    this.form = new FormGroup({
      title: new FormControl(''),
      publishDate: new FormControl(localToday()),
      category: new FormControl('NewFeature'),
      version: new FormControl(''),
      summary: new FormControl(''),
      coverImageUrl: new FormControl(''),
      whatChanged: new FormControl(''),
      whereToFind: new FormControl(''),
      steps: new FormArray([this.stepGroup()]),
      notes: new FormControl(''),
      targetPath: new FormControl(''),
      targetButtonText: new FormControl('')
    });
    REQUIRED_TO_PUBLISH.forEach(field => {
      this.subscriptions.push(this.form.get(field).valueChanges.subscribe(value => {
        if (!isBlank(value)) {
          delete this.errors[field];
        }
      }));
    });
    this.subscriptions.push(this.form.get('targetPath').valueChanges.subscribe(path => {
      const screen = findScreen(path);
      if (screen && isBlank(this.form.value.whereToFind)) {
        this.form.get('whereToFind').setValue(screen.menuPath);
      }
    }));
    const id = this.route.snapshot.params.id;
    if (id) {
      this.loadAnnouncement(Number(id), this.route.snapshot.queryParams.publish === '1');
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  addStep() {
    this.steps.push(this.stepGroup());
  }

  removeStep(index: number) {
    this.steps.removeAt(index);
    if (!this.steps.length) {
      this.addStep();
    }
  }

  moveStep(index: number, offset: number) {
    const step = this.steps.at(index);
    this.steps.removeAt(index);
    this.steps.insert(index + offset, step);
  }

  onCoverSelected(event: Event) {
    this.uploadImage(event, this.form.get('coverImageUrl'), 'cover');
  }

  onStepImageSelected(event: Event, index: number) {
    this.uploadImage(event, this.steps.at(index).get('imageUrl'), `step-${index}`);
  }

  clearImage(control: AbstractControl) {
    control.setValue('');
  }

  save(status: AnnouncementStatus) {
    const errors = this.validate(status);
    if (Object.keys(errors).length) {
      this.showErrors(errors, status);
      return;
    }
    this.errors = {};
    this.isSaving = true;
    const body = { ...this.form.value, status } as Announcement;
    const request = this.announcementId
      ? this.apiService.updateAnnouncement(this.announcementId, body)
      : this.apiService.createAnnouncement(body);
    request.subscribe(() => {
      this.notifications.success(status === 'Published' ? 'Duyuru yayınlandı' : 'Taslak kaydedildi');
      this.router.navigate(['/whats-new']);
    }, error => {
      this.isSaving = false;
      if (error.error && error.error.code === ANNOUNCEMENT_INCOMPLETE_CODE) {
        this.showErrors(this.validate(status), status);
        return;
      }
      this.notifications.error('Hata', 'Duyuru kaydedilemedi.');
    });
  }

  jumpTo(field: string) {
    const group = document.getElementById(`fg-${field}`);
    if (!group) {
      return;
    }
    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = group.querySelector('input, textarea, select') as HTMLElement;
    if (input) {
      setTimeout(() => input.focus(), 300);
    }
    this.shakingField = null;
    setTimeout(() => {
      this.shakingField = field;
      setTimeout(() => {
        this.shakingField = null;
      }, 400);
    });
  }

  private stepGroup(step?: AnnouncementStep): FormGroup {
    return new FormGroup({
      text: new FormControl(step ? step.text || '' : ''),
      imageUrl: new FormControl(step ? step.imageUrl || '' : '')
    });
  }

  private loadAnnouncement(id: number, checkPublish: boolean) {
    this.announcementId = id;
    this.isLoading = true;
    this.apiService.getAnnouncement(id).subscribe(res => {
      const announcement: Announcement = res.data;
      const steps = announcement.steps && announcement.steps.length ? announcement.steps : [null];
      this.form.setControl('steps', new FormArray(steps.map(step => this.stepGroup(step))));
      this.form.patchValue({
        title: announcement.title || '',
        publishDate: announcement.publishDate || '',
        category: announcement.category,
        version: announcement.version || '',
        summary: announcement.summary || '',
        coverImageUrl: announcement.coverImageUrl || '',
        whatChanged: announcement.whatChanged || '',
        whereToFind: announcement.whereToFind || '',
        notes: announcement.notes || '',
        targetPath: announcement.targetPath || '',
        targetButtonText: announcement.targetButtonText || ''
      });
      this.isLoading = false;
      if (checkPublish) {
        const errors = this.validate('Published');
        if (Object.keys(errors).length) {
          setTimeout(() => this.showErrors(errors, 'Published'));
        }
      }
    }, () => {
      this.isLoading = false;
      this.notifications.error('Hata', 'Duyuru bulunamadı.');
      this.router.navigate(['/whats-new']);
    });
    this.apiService.getAnnouncementFeedbacks(id).subscribe(res => {
      this.feedbackCount = res.data.feedbacks.length;
    }, () => {
      this.feedbackCount = 0;
    });
  }

  private validate(status: AnnouncementStatus): { [field: string]: string } {
    const fields = status === 'Published' ? REQUIRED_TO_PUBLISH : ['title'];
    const errors = {};
    fields.filter(field => isBlank(this.form.value[field])).forEach(field => {
      errors[field] = FIELD_ERRORS[field];
    });
    return errors;
  }

  private showErrors(errors: { [field: string]: string }, status: AnnouncementStatus) {
    this.errors = errors;
    this.errorsFor = status;
    const count = Object.keys(errors).length;
    this.notifications.error(`${status === 'Published' ? 'Yayınlanamadı' : 'Kaydedilemedi'}: ${count} alan eksik`);
    setTimeout(() => this.jumpTo(this.errorKeys[0]));
  }

  private uploadImage(event: Event, control: AbstractControl, key: string) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) {
      return;
    }
    if (ANNOUNCEMENT_IMAGE_TYPES.indexOf(file.type) === -1) {
      this.notifications.error('Hata', 'Yalnızca PNG, JPEG, GIF ya da WEBP görsel yüklenebilir.');
      return;
    }
    if (file.size > ANNOUNCEMENT_IMAGE_MAX_BYTES) {
      this.notifications.error('Hata', 'Görsel en fazla 5 MB olabilir.');
      return;
    }
    let url = '';
    this.uploading[key] = true;
    this.apiService.createAnnouncementImageUpload(file.type).pipe(
      switchMap(res => {
        url = res.data.url;
        return this.apiService.uploadAnnouncementImage(res.data.signedUrl, file);
      })
    ).subscribe(() => {
      this.uploading[key] = false;
      control.setValue(url);
    }, () => {
      this.uploading[key] = false;
      this.notifications.error('Hata', 'Görsel yüklenemedi.');
    });
  }
}



// WEBPACK FOOTER //
// /var/www/bizisystem-frontend/src/src/app/main/pages/whats-new/whats-new-editor/whats-new-editor.component.ts